import { Component, Injector, OnDestroy, OnInit } from '@angular/core';
import { CustomColDef, FilterItem } from '@core/types';
import { RegistryService } from '@core/utils';
import { AgGridYaloComponent } from '@shared/ag-components';
import { KeyValueOption } from '@shared/table';
import { Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { DrawersService } from '../drawers.service';
import { FilterDef } from '../drawers.types';
import { InformesFiltersService } from '@pages/dashboard/informes/services/informes-filters.service';

@Component({
  selector: 'app-filters-drawer',
  templateUrl: 'filters-drawer.component.html',
  styleUrls: ['filters-drawer.component.scss'],
})
export class FiltersDrawerComponent implements OnInit, OnDestroy {
  columnDefs: CustomColDef[] = [];

  filtersDefs$;
  filterDefs: FilterDef[] = [];
  name = '';

  private readonly _unsubscribeAll = new Subject<void>();
  private _drawers: DrawersService;

  constructor(
    private _registry: RegistryService,
    private injector: Injector,
    private _informesFiltersService: InformesFiltersService
  ) {
    this._drawers = this.injector.get(DrawersService);
    this.filtersDefs$ = this._drawers.filterDefs$;
  }

  get isFilterActive() {
    return this.columnDefs.some((c) =>
      c.filterParams.filterOptions.some((f) => !f.selected)
    );
  }

  ngOnInit() {
    this._drawers.filterDefs$
      .pipe(
        map((filters) => {
          if (!filters) {
            return null;
          }
          return filters.map(({ filterDefs, ...rest }) => ({
            ...rest,
            filterDefs: filterDefs.map((option) => ({
              ...option,
              field: rest.field,
            })),
          }));
        }),
        takeUntil(this._unsubscribeAll)
      )
      .subscribe((filters) => {
        if (!filters) {
          this.filterDefs = [];
          return;
        }
        this.filterDefs = filters;
      });
    this._drawers.columnDefs$
      .pipe(
        map((data) => {
          if (!data) {
            return null;
          }
          return {
            ...data,
            columnDefs: data.columnDefs.filter(
              (c) => c.filter && c.filterParams
            ),
          };
        }),
        takeUntil(this._unsubscribeAll)
      )
      .subscribe({
        next: (data) => {
          if (!data) {
            this.name = '';
            this.columnDefs = [];
            return;
          }
          const { name, columnDefs } = data;
          this.name = name;
          this.columnDefs = columnDefs.map((column) => ({
            ...column,
            expanded: false,
          }));
        },
        // complete: () => console.log('columnDefs$.complete'),
      });
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  clearFilters() {
    this.filterDefs.forEach(filter => {
      filter.expanded = false;
      filter.filterDefs.forEach(f => f.selected = false);
    });

    const establecimientos = this.filterDefs
      .find(fd => fd.field === 'CodEstablecimiento');
      if (establecimientos) {
        this.toggleSelectAll(establecimientos);
      }

    const puntosEmision = this.filterDefs
      .find(fd => fd.field === 'CodPuntoEmision');
    if (puntosEmision) {
      this.toggleSelectAll(puntosEmision);
    }
    this._drawers.changeSelectedOptions([]);
  }

  closeDrawer() {
    this._drawers.changeSelectedOptions([]);

    const drawer = this._drawers.getDrawer('filtersDrawer');
    if (drawer) {
      drawer.close();
    }
  }

  onFilterChanged() {
    const agGridYalo = this._registry.getComponent<AgGridYaloComponent<any>>(
      this.name
    );
    const filterOptions: FilterItem[] = [...this.columnDefs]
      .map((c) => c.filterParams.filterOptions)
      .reduce((acc, val) => acc.concat(val), []);
    agGridYalo.filterChanged(filterOptions);
    this._drawers.changeSelectedOptions(filterOptions);

  }

  onOptionChanged(option: KeyValueOption) {
    this._drawers.changeFilterOption(option);
  }

  onCheckboxChanged(option: KeyValueOption) {
    const expandedStates = this.filterDefs.map(filter => ({
      field: filter.field,
      expanded: filter.expanded
    }));

    if (option.field === 'CodEstablecimiento') {
      this.handleEstablishmentSelection(option);
    } else if (option.field === 'CodPuntoEmision') {
      this.handlePointOfSaleSelection(option);
    }

    this.filterDefs.forEach(filter => {
      const savedState = expandedStates.find(state => state.field === filter.field);
      if (savedState) {
        filter.expanded = savedState.expanded;
      }
    });
  }

  handleEstablishmentSelection(option: KeyValueOption) {
    const selectedEstablecimientos = this.filterDefs
    .filter(fd => fd.type === 'checkbox' && fd.field === 'CodEstablecimiento')
    .flatMap(fd => fd.filterDefs)
    .filter(f => f.selected)
    .map(f => f.value);

    const unselectedPoints = this.filterDefs
      .filter(fd => fd.type === 'checkbox' && fd.field === 'CodPuntoEmision')
      .flatMap(fd => fd.filterDefs)
      .filter(punto => !punto.selected)
      .map(punto => punto.value);

    this._informesFiltersService.updateFilters(selectedEstablecimientos, undefined);

  //   const filteroptions = this.filterDefs
  //  .filter(fd => fd.type === 'checkbox' && fd.field === 'CodPuntoEmision')
  //  .flatMap(fd => fd.filterDefs)
  //  .filter(f => f.selected);
  //  this._drawers.changeSelectedOptions(filteroptions);

  }

  handlePointOfSaleSelection(option: KeyValueOption) {
    const selectedPointsOfSale = this.filterDefs
    .filter(fd => fd.type === 'checkbox' && fd.field === 'CodPuntoEmision')
    .flatMap(fd => fd.filterDefs)
    .filter(f => f.selected)
    .map(f => f.value);

  const selectedEstablecimientos = this.filterDefs
    .filter(fd => fd.type === 'checkbox' && fd.field === 'CodEstablecimiento')
    .flatMap(fd => fd.filterDefs)
    .filter(f => f.selected)
    .map(f => f.value);

    this._informesFiltersService.updateFilters(selectedEstablecimientos, selectedPointsOfSale);

    // const filterOptions = this.filterDefs
    //   .filter(fd => fd.type === 'checkbox' && fd.field === 'CodPuntoEmision')
    //   .flatMap(fd => fd.filterDefs)
    //   .filter(f => f.selected);
    // this._drawers.changeSelectedOptions(filterOptions);
  }


  isAllSelected(filter: FilterDef): boolean {
    return filter.filterDefs.length > 0 && filter.filterDefs.every(option => option.selected);
  }

  toggleSelectAll(filter: FilterDef): void {
    const shouldSelect = !this.isAllSelected(filter);

    const expandedStates = this.filterDefs.map(f => ({
      field: f.field,
      expanded: f.expanded
    }));

    filter.filterDefs.forEach(option => {
      option.selected = shouldSelect;
    });

    if (filter.field === 'CodEstablecimiento') {
      const selectedEstablecimientos = filter.filterDefs
        .filter(option => option.selected)
        .map(option => option.value);

      this._informesFiltersService.updateFilters(selectedEstablecimientos, undefined);

      // const filterOptions = filter.filterDefs
      //   .filter(f => f.selected);
      // this._drawers.changeSelectedOptions(filterOptions);
    } else if (filter.field === 'CodPuntoEmision') {
      const selectedPointsOfSale = filter.filterDefs
        .filter(f => f.selected)
        .map(f => f.value);

      const selectedEstablecimientos = this.filterDefs
        .filter(fd => fd.type === 'checkbox' && fd.field === 'CodEstablecimiento')
        .flatMap(fd => fd.filterDefs)
        .filter(f => f.selected)
        .map(f => f.value);

      this._informesFiltersService.updateFilters(selectedEstablecimientos, selectedPointsOfSale);

      // const filterOptions = filter.filterDefs
      //   .filter(f => f.selected);
      // this._drawers.changeSelectedOptions(filterOptions);
    }

    this.filterDefs.forEach(f => {
      const savedState = expandedStates.find(state => state.field === f.field);
      if (savedState) {
        f.expanded = savedState.expanded;
      }
    });
  }

  hasSelectedEstablishments(): boolean {
    const establishments = this.filterDefs.find(fd => fd.field === 'CodEstablecimiento');
    return establishments?.filterDefs.some(def => def.selected) || false;
  }
}
